import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { login, sendMagicLink, checkEmail } from '../services/api';
// import ThemeToggle from '../components/ThemeToggle'; // dark mode disabled for now
import { useTheme } from '../context/ThemeContext';
import {
  Mail,
  Loader2,
  RefreshCw,
  ArrowLeft,
  ArrowRight,
  Lock,
  Eye,
  EyeOff,
} from 'lucide-react';

const TOKEN_STORAGE_KEY = 'auth_token';

function getValidRedirect(redirect: string | null): string | null {
  if (!redirect || typeof redirect !== 'string') return null;
  const decoded = decodeURIComponent(redirect.trim());
  if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded;
  return null;
}

type Step = 'form' | 'password' | 'magic';

const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();

  const [step, setStep] = useState<Step>('form');

  const [email, setEmail] = useState('');
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [isSendingMagicFromPassword, setIsSendingMagicFromPassword] = useState(false);

  const [resendCountdown, setResendCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState('');

  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const redirectTo = getValidRedirect(searchParams.get('redirect')) || '/';

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      navigate(redirectTo, { replace: true });
      return;
    }
    if (redirectTo.startsWith('/auth/verify')) {
      navigate(redirectTo, { replace: true });
    }
  }, [navigate, redirectTo]);

  useEffect(() => {
    if (step === 'form') emailInputRef.current?.focus();
    if (step === 'password') setTimeout(() => passwordInputRef.current?.focus(), 50);
  }, [step]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const t = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  const handleContinueWithEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setEmailError('Please enter your email address.');
      return;
    }
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError('');
    setIsLoadingEmail(true);

    try {
      const { hasPassword } = await checkEmail(email);

      if (hasPassword) {
        setStep('password');
      } else {
        await sendMagicLink(email, redirectTo);
        setResendCountdown(30);
        setMagicLinkError('');
        setStep('magic');
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong. Please try again.';
      setEmailError(message);
    } finally {
      setIsLoadingEmail(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setPasswordError('Please enter your password.');
      return;
    }
    setPasswordError('');
    setIsLoadingPassword(true);
    try {
      await login(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Incorrect password. Please try again.';
      setPasswordError(message);
    } finally {
      setIsLoadingPassword(false);
    }
  };

  const handleForgotPassword = async () => {
    setIsSendingMagicFromPassword(true);
    try {
      await sendMagicLink(email, redirectTo);
      setResendCountdown(30);
      setMagicLinkError('');
      setStep('magic');
    } catch (err: any) {
      setPasswordError(
        err?.response?.data?.message ||
        err?.message ||
        'Unable to send sign-in link. Please try again.',
      );
    } finally {
      setIsSendingMagicFromPassword(false);
    }
  };

  const handleResend = async () => {
    if (resendCountdown > 0) return;
    setIsResending(true);
    try {
      await sendMagicLink(email, redirectTo);
      setResendCountdown(30);
      setMagicLinkError('');
    } catch (err: any) {
      setMagicLinkError(
        err?.response?.data?.message ||
        err?.message ||
        'Unable to resend. Please try again.',
      );
    } finally {
      setIsResending(false);
    }
  };

  const Logo = () => (
    <header className="w-full py-8 flex justify-center">
      <div className="h-6">
        <img
          src={isDark ? '/turing-logo-white.svg' : '/turing-logo.svg'}
          alt="Turing Logo"
          className="h-full object-contain"
        />
      </div>
    </header>
  );

  const FooterEl = () => (
    <footer className="py-6 flex flex-col items-center gap-2">
      <svg
        viewBox="0 0 76 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-auto text-zinc-300 dark:text-zinc-700"
      >
        <path
          d="M37.673 0c1.715 0 3.368 0.018 5.022 0.06 -0.006 0.004 -1.074 0.303 -1.445 0.377C25.207 0.104 11.212 2.563 11.212 5.389c0 2.945 14.922 5.333 33.329 5.333 13.714 0 25.492 -1.327 30.608 -3.22 -1.324 3.653 -17.596 6.541 -37.477 6.541 -3.606 0 -7.093 -0.095 -10.394 -0.272 8.132 3.627 14.933 8.388 14.933 13.801 0 0.04 0.04 8.662 0.04 19.477 -0.113 0 -0.28 -2.225 -0.28 -2.225C40.164 10.308 -2.646 16.47 0.129 6.699 1.2 2.928 17.763 0 37.673 0"
          fill="currentColor"
        />
      </svg>
      <p className="text-xs text-zinc-400 dark:text-zinc-600">
        &copy; {new Date().getFullYear()} Turing Enterprises Inc.
      </p>
    </footer>
  );

  // ── Step 1: Email form ──
  if (step === 'form') {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-background flex flex-col font-sans text-zinc-900 dark:text-foreground relative">
        {/* Dark mode disabled for now
        <div className="absolute top-4 right-4 z-10">
          <ThemeToggle />
        </div>
        */}
        <Logo />
        <main className="flex-1 flex items-center justify-center px-4 pb-12 pt-4">
          <div className="w-full max-w-sm">
            <div className="w-full bg-white dark:bg-card rounded-2xl shadow-md border border-zinc-200 dark:border-border overflow-hidden">
              <div className="p-8 space-y-5">
                <div className="text-center">
                  <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight">
                    Sign in to Turing
                  </h1>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1.5">
                    Curated Talent Portal
                  </p>
                </div>

                <form onSubmit={handleContinueWithEmail} className="space-y-4" noValidate>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5"
                    >
                      Email address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                      <input
                        ref={emailInputRef}
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) setEmailError('');
                        }}
                        placeholder="name@company.com"
                        autoComplete="email"
                        autoFocus
                        aria-describedby={emailError ? 'email-error' : 'email-hint'}
                        aria-invalid={!!emailError}
                        className={`w-full pl-9 pr-3 py-2.5 bg-zinc-50 dark:bg-secondary border rounded-lg text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 transition-colors ${
                          emailError
                            ? 'border-red-300 dark:border-red-700 focus:ring-red-500/20'
                            : 'border-zinc-200 dark:border-border focus:ring-zinc-900/10 dark:focus:ring-zinc-100/10 focus:border-zinc-400 dark:focus:border-zinc-500'
                        }`}
                      />
                    </div>
                    {emailError ? (
                      <p id="email-error" role="alert" className="mt-1.5 text-xs text-red-500 dark:text-red-400">
                        {emailError}
                      </p>
                    ) : (
                      <p id="email-hint" className="mt-1.5 text-xs text-zinc-400 dark:text-zinc-500">
                        Enter your work email to continue.
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoadingEmail}
                    className="mt-4 w-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-medium py-3 px-4 rounded-xl hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-100 focus:ring-offset-2 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isLoadingEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      'Continue with email'
                    )}
                  </button>
                </form>

                <p className="text-center text-xs text-zinc-400 dark:text-zinc-500 leading-relaxed">
                  By signing in you agree to Turing's{' '}
                  <a
                    href="https://www.turing.com/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.turing.com/policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-center gap-1 text-xs text-zinc-400 dark:text-zinc-500">
              Need help signing in?{' '}
              <a
                href="mailto:support@turing.com"
                className="underline hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
              >
                Contact us
              </a>
            </div>
          </div>
        </main>
        <FooterEl />
      </div>
    );
  }

  // ── Step 2a: Password login ──
  if (step === 'password') {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-background flex flex-col font-sans text-zinc-900 dark:text-foreground relative">
        {/* Dark mode disabled for now
        <div className="absolute top-4 right-4 z-10">
          <ThemeToggle />
        </div>
        */}
        <Logo />
        <main className="flex-1 flex items-center justify-center px-4 pb-12 pt-4">
          <div className="w-full max-w-sm">
            <div className="w-full bg-white dark:bg-card rounded-2xl shadow-md border border-zinc-200 dark:border-border overflow-hidden">
              <div className="p-8 space-y-5">
                <button
                  onClick={() => {
                    setStep('form');
                    setPassword('');
                    setPasswordError('');
                  }}
                  className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors -ml-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>

                <div>
                  <div className="w-12 h-12 bg-zinc-100 dark:bg-secondary rounded-full flex items-center justify-center mb-4">
                    <Lock className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight">
                    Welcome back
                  </h2>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                    Signing in as{' '}
                    <span className="font-medium text-zinc-700 dark:text-zinc-300">{email}</span>
                  </p>
                </div>

                <form onSubmit={handlePasswordLogin} className="space-y-4" noValidate>
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5"
                    >
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                      <input
                        ref={passwordInputRef}
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (passwordError) setPasswordError('');
                        }}
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        aria-invalid={!!passwordError}
                        className={`w-full pl-9 pr-10 py-2.5 bg-zinc-50 dark:bg-secondary border rounded-lg text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 transition-colors ${
                          passwordError
                            ? 'border-red-300 dark:border-red-700 focus:ring-red-500/20'
                            : 'border-zinc-200 dark:border-border focus:ring-zinc-900/10 dark:focus:ring-zinc-100/10 focus:border-zinc-400 dark:focus:border-zinc-500'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordError && (
                      <p role="alert" className="mt-1.5 text-xs text-red-500 dark:text-red-400">
                        {passwordError}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoadingPassword}
                    className="w-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-medium py-3 px-4 rounded-xl hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-100 focus:ring-offset-2 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isLoadingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        Sign in
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>

                <div className="text-center pt-1 border-t border-zinc-100 dark:border-border">
                  <button
                    onClick={handleForgotPassword}
                    disabled={isSendingMagicFromPassword}
                    className="text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors focus:outline-none focus:underline disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  >
                    {isSendingMagicFromPassword && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {isSendingMagicFromPassword ? 'Sending sign-in link...' : 'Forgot password? Sign in with magic link'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
        <FooterEl />
      </div>
    );
  }

  // ── Step 2b: Magic link sent ──
  if (step === 'magic') {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-background flex flex-col font-sans text-zinc-900 dark:text-foreground relative">
        {/* Dark mode disabled for now
        <div className="absolute top-4 right-4 z-10">
          <ThemeToggle />
        </div>
        */}
        <Logo />
        <main className="flex-1 flex items-center justify-center px-4 pb-12 pt-4">
          <div className="w-full max-w-sm space-y-4">
            <div className="w-full bg-white dark:bg-card rounded-2xl shadow-md border border-zinc-200 dark:border-border overflow-hidden">
              <div className="p-8 space-y-6">
                <button
                  onClick={() => setStep('form')}
                  className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors -ml-1"
                  aria-label="Back"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>

                <div className="text-center space-y-1.5">
                  <div className="w-14 h-14 bg-zinc-100 dark:bg-secondary rounded-full flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-6 h-6 text-zinc-600 dark:text-zinc-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight">
                    Check your inbox
                  </h2>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">
                    We sent a magic link to
                  </p>
                  <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200 bg-zinc-50 dark:bg-secondary px-3 py-1.5 rounded-lg inline-block border border-zinc-200 dark:border-border">
                    {email}
                  </p>
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 pt-1">
                    The link expires in 24 hours. Check your spam folder if you don't see it.
                  </p>
                </div>

                {magicLinkError && (
                  <p role="alert" className="text-center text-xs text-red-500 dark:text-red-400">
                    {magicLinkError}
                  </p>
                )}

                <div className="text-center space-y-2 pt-1 border-t border-zinc-100 dark:border-border">
                  <button
                    onClick={handleResend}
                    disabled={resendCountdown > 0 || isResending}
                    className="py-2 flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mx-auto focus:outline-none focus:underline"
                  >
                    {isResending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5" />
                    )}
                    {isResending
                      ? 'Resending...'
                      : resendCountdown > 0
                        ? `Resend link in ${resendCountdown}s`
                        : 'Resend magic link'}
                  </button>
                  <button
                    onClick={() => {
                      setStep('form');
                      setResendCountdown(0);
                    }}
                    className="text-xs text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors focus:outline-none focus:underline"
                  >
                    Use a different email
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
        <FooterEl />
      </div>
    );
  }

  return null;
};

export default LoginPage;
